import { forwardRef, useState } from "react";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  TextInput,
  View,
  type TextInputProps,
  type ViewStyle,
} from "react-native";
import { Search, X } from "lucide-react-native";
import { colors, semanticColors, typography } from "../theme/tokens";
import { useInputFontSize } from "../utils/inputFontSize";
import { MoonFieldInput } from "./MoonFieldInput";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

/**
 * Text input shaped for search affordances. Audit task 2.1.9.
 *
 * Canonicalises the search-row pattern from SearchSheet / GlossaryScreen /
 * AddMedicationModal / payer-search etc.:
 *   - magnifying glass icon left
 *   - placeholder ("Search…")
 *   - optional clear button right (when value is non-empty)
 *   - optional loading spinner replacing the clear button while async
 *     search results resolve
 *
 * Consumers control `value` / `onChangeText` as with any controlled input.
 * Pass `onClear` to enable the trailing X button; otherwise it stays
 * minimal.
 *
 * State coverage (Phase 1.0 matrix):
 *   - idle / focused: visible by border tint swap
 *   - loading: spinner replaces clear button
 *   - disabled: opacity 0.5, input non-editable
 *   - error: pass `error={true}` to render a coral border (rare for
 *     search; helpful when no-results is treated as an error state)
 *   - tone="glass": uses the glass card treatment used by discovery lists
 */
type Props = Omit<TextInputProps, "style" | "value" | "onChangeText"> & {
  value: string;
  onChangeText: (text: string) => void;
  /** Called when the user taps the trailing clear (X) button. Pass to
   *  enable the button; omit to hide it. */
  onClear?: () => void;
  /** What a screen reader calls the clear button. Defaults to "Clear search". */
  clearAccessibilityLabel?: string;
  /** Stable analytics identifier for the clear button. Defaults to `clear_search`. */
  clearAction?: string;
  /** Replace the trailing button with a spinner. */
  loading?: boolean;
  /** Show a coral error border. */
  error?: boolean;
  /** Disable input + presses. */
  disabled?: boolean;
  /** Visual context for the search field. */
  tone?: "default" | "glass";
  /** Container style override. */
  style?: ViewStyle;
};

export const MoonSearchInput = forwardRef<TextInput, Props>(
  function MoonSearchInput(
    {
      value,
      onChangeText,
      placeholder = "Search…",
      onClear,
      clearAccessibilityLabel = "Clear search",
      clearAction = "clear_search",
      loading,
      error,
      disabled,
      tone = "default",
      style,
      onFocus,
      onBlur,
      ...rest
    },
    ref,
  ) {
    const fontSize = useInputFontSize();
    const capturePress = usePressCapture("MoonSearchInput");
    const [focused, setFocused] = useState(false);
    const showClear = !!onClear && value.length > 0 && !loading;
    const isGlass = tone === "glass";
    return (
      <MoonFieldInput
        hasError={error}
        focused={focused}
        focusedBorderColor={isGlass ? semanticColors.border.focus : undefined}
        paddingX={12}
        paddingY={0}
        style={{
          ...(isGlass && {
            borderRadius: 16,
            backgroundColor: semanticColors.surface.glass,
            borderColor: error
              ? semanticColors.status.danger
              : focused
                ? semanticColors.border.focus
                : semanticColors.border.glass,
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 8 },
            shadowOpacity: 0.08,
            shadowRadius: 32,
            ...(Platform.OS === "web" && {
              backdropFilter: "blur(60px)",
              WebkitBackdropFilter: "blur(60px)",
            }),
          }),
          opacity: disabled ? 0.5 : 1,
          ...style,
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 10,
            height: 44,
          }}
        >
          <Search
            size={16}
            color={colors.textLight}
            strokeWidth={1.5}
            style={{ flexShrink: 0 }}
          />
          <TextInput
            ref={ref}
            value={value}
            onChangeText={onChangeText}
            onFocus={(event) => {
              setFocused(true);
              onFocus?.(event);
            }}
            onBlur={(event) => {
              setFocused(false);
              onBlur?.(event);
            }}
            placeholder={placeholder}
            placeholderTextColor={colors.textFaint}
            editable={!disabled}
            autoCorrect={false}
            spellCheck={false}
            style={{
              flex: 1,
              // Lets the text give way in a narrow menu instead of squeezing
              // the icon or the clear button.
              minWidth: 0,
              fontFamily: typography.families.sans,
              fontSize,
              color: colors.text,
              ...(Platform.OS === "web"
                ? { outlineStyle: "none" as const }
                : {}),
            }}
            {...rest}
          />
          {loading ? (
            <ActivityIndicator
              size="small"
              color={semanticColors.accent.default}
            />
          ) : showClear ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={clearAccessibilityLabel}
              onPress={() => {
                capturePress(pressLabel(clearAccessibilityLabel), clearAction);
                onClear?.();
              }}
              hitSlop={8}
              style={{
                width: 22,
                height: 22,
                borderRadius: 11,
                backgroundColor: "rgba(0, 0, 0, 0.06)",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <X size={12} color={colors.textFaint} strokeWidth={2.5} />
            </Pressable>
          ) : null}
        </View>
      </MoonFieldInput>
    );
  },
);
